import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Mail, Lock, ArrowRight, LineChart, BarChart3, Leaf, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LOGO = "https://customer-assets.emergentagent.com/job_monitor-adybo/artifacts/tadicmii_Adybo%20Logo%20Colori%20Bianco%20R.png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_monitor-adybo/artifacts/tnryhjnp_Adybo%20Logo%20Colori%20Nero%20R.png";

const SOULS = [
  { key: "market", Icon: LineChart, title: "Market Intelligence", text: "Quotazioni, forward, report e news dei mercati energetici", from: "#3B8BFF", to: "#0B4DFF", color: "#3285FD" },
  { key: "analytics", Icon: BarChart3, title: "Energy Analytics", text: "Consumi, costi, curve di carico e Forecast vs Budget", from: "#B26BFF", to: "#7B2CFF", color: "#8B32FB" },
  { key: "esg", Icon: Leaf, title: "Sostenibilità", text: "KPI ambientali, emissioni CO₂ e report ESG", from: "#22C997", to: "#059669", color: "#10B981" },
];

const inputCls = "w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition";

function Souls() {
  return (
    <div className="space-y-4" data-testid="login-souls">
      {SOULS.map(({ key, Icon, title, text, from, to, color }, i) => (
        <div key={key} className="fade-up opacity-0 flex items-center gap-5 rounded-2xl p-5 backdrop-blur-md transition-transform hover:-translate-y-0.5"
          style={{ animationDelay: `${150 + i * 120}ms`, background: `linear-gradient(90deg, ${color}26, rgba(255,255,255,0.04))`, border: `1px solid ${color}80`, boxShadow: `0 0 28px ${color}22` }}
          data-testid={`login-soul-${key}`}>
          <div className="w-16 h-16 shrink-0 rounded-2xl flex items-center justify-center text-white" style={{ background: `linear-gradient(135deg, ${from}, ${to})`, boxShadow: `0 10px 28px ${to}66` }}>
            <Icon size={28} strokeWidth={2} />
          </div>
          <div>
            <div className="font-display text-xl font-bold text-white tracking-tight">{title}</div>
            <div className="text-[15px] text-white/75 leading-snug mt-0.5">{text}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Hero() {
  return (
    <div className="hidden lg:flex relative overflow-hidden items-center justify-center p-16" style={{ background: "radial-gradient(ellipse at 80% 10%, #3A1C9E 0%, transparent 45%), radial-gradient(ellipse at 10% 90%, #1E3FBF 0%, transparent 45%), #0A0F3D" }} data-testid="login-hero">
      <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full border border-purple-400/20 bg-purple-600/20 blur-2xl" />
      <div className="absolute -bottom-40 -left-24 w-[34rem] h-[20rem] rounded-full bg-blue-500/25 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-2/3 h-1/3 opacity-40" style={{ backgroundImage: "radial-gradient(#8FB3FF 1px, transparent 1px)", backgroundSize: "14px 14px", maskImage: "linear-gradient(to top left, black, transparent 70%)", WebkitMaskImage: "linear-gradient(to top left, black, transparent 70%)" }} />
      <svg className="absolute bottom-0 left-0 w-full h-1/2 pointer-events-none" viewBox="0 0 800 400" preserveAspectRatio="none" aria-hidden="true">
        <defs><linearGradient id="lw" x1="0" x2="1"><stop offset="0%" stopColor="#3285FD" stopOpacity="0" /><stop offset="55%" stopColor="#5B7CFF" stopOpacity="0.9" /><stop offset="100%" stopColor="#B26BFF" stopOpacity="0.9" /></linearGradient></defs>
        <path d="M0 360 C 220 380, 420 330, 560 260 S 760 160, 800 140" fill="none" stroke="url(#lw)" strokeWidth="2.5" style={{ filter: "drop-shadow(0 0 8px #6D8BFF)" }} />
        <path d="M0 390 C 260 400, 460 360, 600 300 S 780 220, 800 210" fill="none" stroke="url(#lw)" strokeWidth="1.2" opacity="0.6" />
      </svg>
      <div className="relative z-10 w-full max-w-lg">
        <img src={LOGO} alt="Adybo" className="w-72 mx-auto mb-12 drop-shadow-2xl" />
        <Souls />
        <div className="mx-auto mt-8 mb-6 w-20 h-px bg-white/25" />
        <p className="text-center text-lg text-white/85 leading-relaxed" data-testid="login-tagline">
          <span className="font-semibold text-white">Un’unica piattaforma</span> per monitorare<br />mercati, consumi, costi e KPI ambientali.
        </p>
      </div>
    </div>
  );
}

function ForgotDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="login-forgot-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Password dimenticata?</DialogTitle>
          <DialogDescription className="text-slate-600 leading-relaxed pt-2">
            Contatta il tuo referente Adybo: genererà per te una nuova password dal Backoffice. Al primo accesso potrai cambiarla dal tuo profilo.
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) setErr(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-10 sm:px-12" style={{ background: "linear-gradient(160deg, #F7F8FD 0%, #EEF1FA 100%)" }}>
        <div className="w-full max-w-[34rem] bg-white rounded-[28px] shadow-[0_24px_70px_-20px_rgba(15,23,42,0.18)] px-8 sm:px-12 py-12 fade-up" data-testid="login-card">
          <div className="flex flex-col items-center text-center">
            <img src={LOGO_DARK} alt="Adybo" className="h-24 w-auto" data-testid="login-logo" />
            <div className="mt-4 px-8 py-2 rounded-full shadow-[0_6px_20px_-8px_rgba(123,44,255,0.35)]" style={{ background: "linear-gradient(90deg, #EAF1FF, #F3EAFF)" }}>
              <span className="font-display font-bold text-base tracking-[0.06em] uppercase brand-gradient-text" data-testid="login-product">Energy Monitor</span>
            </div>
            <h1 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Accedi alla piattaforma</h1>
            <p className="mt-3 text-base sm:text-lg text-slate-500 leading-relaxed">Controlla mercati energetici, consumi, costi<br className="hidden sm:block" /> e sostenibilità in un’unica soluzione.</p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-3.5 text-slate-400" size={18} />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="nome@azienda.com" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-3.5 text-slate-400" size={18} />
                <input type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} pr-11`} placeholder="••••••••" data-testid="login-password-input" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-3 p-0.5 text-slate-400 hover:text-slate-600 transition-colors" aria-label={show ? "Nascondi password" : "Mostra password"} data-testid="login-password-toggle">
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="text-right mt-2">
                <button type="button" onClick={() => setForgot(true)} className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline" data-testid="login-forgot-link">Password dimenticata?</button>
              </div>
            </div>

            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}

            <button type="submit" disabled={busy} className="btn-brand w-full flex items-center justify-center gap-2 py-3.5 mt-2" data-testid="login-submit-button">
              {busy ? "Accesso…" : "Accedi al portale"} <ArrowRight size={18} />
            </button>
          </form>

          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-500" data-testid="login-reserved">
            <Lock size={15} /> Accesso riservato ai clienti Adybo
          </div>
        </div>
      </div>
      <Hero />
      <ForgotDialog open={forgot} onOpenChange={setForgot} />
    </div>
  );
}
